import React, { useRef, useEffect, useState } from "react";
import {
  YAxis,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ScatterChart,
  ReferenceArea,
  ZAxis,
  ReferenceLine,
} from "recharts";
import PropTypes from "prop-types";
import { schemeTableau10 } from "d3-scale-chromatic";
import CustomTooltip from "./Tooltip";
import ChartWrapper from "./ChartWrapper";
import ScatterJitter from "./ScatterJitter";
import getNthDigit from "../../utils/dataHelpers/getNthDigit";
import Annotation from "./Annotation";

const genCategoryEnum = (data) => {
  const hash = {};
  data.forEach((item) => {
    const { label, categoryVal } = item;
    hash[categoryVal] = label;
  });
  return hash;
};

let canvas;
let root;

const getFont = () => {
  const div = root || document.querySelector("#app"); // root could be your react mount node or a node expected to exist
  const style = window.getComputedStyle(div);
  return style.getPropertyValue("font");
};

const getTextWidth = (text) => {
  canvas = canvas || document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = getFont();
  const metric = context.measureText(text);
  return Math.ceil(metric.width * 1.1); // measureText method's return value may be slightly underestimated, add some pixels or scale it
};

const Chart = (props) => {
  const {
    title,
    data,
    xAxisKey,
    jitterKey,
    jitterVar,
    dotSize,
    xAxisLabel,
    xAxisTickFormatter,
    xAxisHeight,
    tooltipSchema,
  } = props;
  const [chartWidth, setChartWidth] = useState(0);
  const categoryEnum = genCategoryEnum(data);
  const elChart = useRef();
  const firstRowOfData = data[data.length - 1];
  const sortedFirstRow = firstRowOfData.data.sort(
    (a, b) => b.workload - a.workload
  );
  const highestVal = sortedFirstRow[0][xAxisKey];
  const { meanCountyWorkload } = firstRowOfData;
  const totalCategories = data.length;
  useEffect(() => {
    const { width } = elChart.current.container.getBoundingClientRect();
    setChartWidth(width);
  }, []);
  // calculate yAxis width by getting size of longest label
  const labelWidths = data.map((item) => getTextWidth(item.label));
  const yAxisWidth = Math.max(...labelWidths) + 3;
  return (
    <ChartWrapper title={title}>
      <ResponsiveContainer ref={elChart}>
        <ScatterChart
          width={500}
          height={800}
          data={data}
          margin={{
            top: 40,
            right: 9,
            left: 0,
            bottom: 0,
          }}
        >
          <XAxis
            tick={{ fontSize: 16 }}
            axisLine={false}
            tickLine={false}
            type="number"
            dataKey={xAxisKey}
            label={xAxisLabel}
            tickFormatter={xAxisTickFormatter}
            height={xAxisHeight}
            domain={["dataMin", "auto"]}
            padding={{ left: 5, right: 5 }}
          />
          <YAxis
            ticks={data.map((_, idx) => idx + 1)}
            tick={{ fontSize: 16 }}
            interval={0}
            tickFormatter={(x) => categoryEnum[x]}
            tickLine={false}
            axisLine={false}
            width={yAxisWidth}
            type="number"
            dataKey={jitterKey}
            domain={["dataMin", "dataMax"]}
          />
          <ZAxis range={[dotSize, dotSize]} />
          <CartesianGrid horizontal={false} vertical={false} />
          {data.map((datum) => {
            const {
              meanCountyWorkload: avgCountyWorkload,
              categoryVal,
            } = datum;

            return (
              <ReferenceArea
                key={`area-${categoryVal}`}
                ifOverflow="extendDomain"
                x1={avgCountyWorkload * 0.85}
                x2={avgCountyWorkload * 1.15}
                y1={categoryVal - 0.5}
                y2={categoryVal + 0.5}
                stroke={null}
                strokeOpacity={0.3}
                fillOpacity={0.4}
              />
            );
          })}
          {data.map(({ categoryVal }) => {
            return (
              <ReferenceLine
                key={`line-${categoryVal}`}
                y={categoryVal}
                strokeDasharray="3 3"
              />
            );
          })}
          {Annotation({
            chartWidth,
            x: meanCountyWorkload,
            y: totalCategories + 0.5,
            textLabels: ["Recommended", "workload"],
            arrowLineWidth: 15,
            bottomPadding: 3,
            forceLeft: true,
          })}
          {Annotation({
            chartWidth,
            y: data.length,
            x: highestVal,
            textLabels: ["Judges"],
            arrowLineWidth: 15,
            collisionXPos: meanCountyWorkload * 1.15,
          })}

          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={<CustomTooltip tooltipSchema={tooltipSchema} />}
          />
          {data.map((series, idx) => {
            const { label, data: seriesData, categoryVal } = series;

            return ScatterJitter({
              key: label,
              label,
              categoryVal,
              jitterKey,
              jitterVar,
              data: seriesData,
              fill: schemeTableau10[getNthDigit(idx, 1)],
            });
          })}
        </ScatterChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};

Chart.defaultProps = {
  title: null,
  xAxisTickFormatter: null,
  xAxisLabel: null,
  xAxisHeight: 30,
  jitterKey: "y",
  jitterVar: 0.05,
  dotSize: 50,
  tooltipSchema: null,
};

Chart.propTypes = {
  title: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  xAxisKey: PropTypes.string.isRequired,
  jitterKey: PropTypes.string,
  jitterVar: PropTypes.number,
  dotSize: PropTypes.number,
  xAxisLabel: PropTypes.string,
  xAxisHeight: PropTypes.number,
  xAxisTickFormatter: PropTypes.func,
  tooltipSchema: PropTypes.arrayOf(PropTypes.object),
};

// Memoizing component to prevent needless re-renders which move around scatter dots.
export default Chart;
